import {
  HIDDEN,
  NAV_SEARCH,
  SEARCH_ONLY,
  type AdminRouteDefinition,
} from './admin-route-types';

export const PRIMARY_ADMIN_ROUTES = [
  // People & access
  {
    id: 'users',
    label: 'Employees',
    description: 'Manage employee cell numbers, sign-in access, roles, and app setup links.',
    group: 'people-access',
    icon: 'Users',
    lifecycle: 'canonical',
    pathPattern: '/admin/users',
    href: '/admin/users',
    searchTerms: ['employees', 'team', 'staff', 'accounts', 'access', 'roles', 'permissions', 'cell', 'phone', 'mobile', 'invite', 'install', 'pwa'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'account-security',
    label: 'Account security',
    description: 'Configure sign-in protection and two-factor authentication.',
    group: 'people-access',
    icon: 'ShieldCheck',
    lifecycle: 'canonical',
    pathPattern: '/settings/security',
    href: '/settings/security',
    searchTerms: ['2fa', 'two factor', 'authentication', 'login', 'password'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'authenticated',
  },
  {
    id: 'study-access',
    label: 'Study access',
    description: 'Grant or revoke access to certification study material.',
    group: 'people-access',
    icon: 'KeyRound',
    lifecycle: 'canonical',
    pathPattern: '/admin/learn',
    href: '/admin/learn',
    searchTerms: ['learn', 'training', 'certification', 'license'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'audit-log',
    label: 'Audit log',
    description: 'Review administrative actions and recorded system changes.',
    group: 'people-access',
    icon: 'FileText',
    lifecycle: 'canonical',
    pathPattern: '/admin/audit',
    href: '/admin/audit',
    searchTerms: ['history', 'changes', 'activity', 'who changed'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },

  // Catalog & policy
  {
    id: 'pricing',
    label: 'Catalog & pricing',
    description: 'Configure global rates, tax rules, discounts, and service definitions.',
    group: 'catalog-policy',
    icon: 'DollarSign',
    lifecycle: 'canonical',
    pathPattern: '/admin/pricing',
    href: '/admin/pricing',
    searchTerms: ['settings', 'tax', 'discounts', 'production rates'],
    visibility: { navigation: true, commandPalette: true, suggested: true },
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'products',
    label: 'Product catalog',
    description: 'Manage product definitions, supplier pricing, and compliance metadata.',
    group: 'catalog-policy',
    icon: 'Package',
    lifecycle: 'canonical',
    pathPattern: '/admin/products',
    href: '/admin/products',
    searchTerms: ['product catalog', 'chemicals', 'fertilizer', 'materials', 'supplier', 'epa'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'packages',
    label: 'Level-Pay package tiers',
    description: 'Manage approved annual values and included visits for Level-Pay enrollment.',
    group: 'catalog-policy',
    icon: 'ClipboardList',
    lifecycle: 'canonical',
    pathPattern: '/admin/packages',
    href: '/admin/packages',
    searchTerms: ['level pay', 'monthly package', 'annual visits', 'package tier'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'package-create',
    label: 'Create package',
    description: 'Legacy package-creation address retained for old bookmarks.',
    group: 'catalog-policy',
    icon: 'ClipboardList',
    lifecycle: 'compatibility',
    pathPattern: '/admin/packages/new',
    href: '/admin/packages/new',
    canonicalHref: '/admin/packages',
    searchTerms: ['new package', 'level pay builder'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'package-tiers-compatibility',
    label: 'Package tiers',
    description: 'Legacy package-tier address retained for old bookmarks.',
    group: 'catalog-policy',
    icon: 'Package',
    lifecycle: 'compatibility',
    pathPattern: '/admin/package-tiers',
    href: '/admin/package-tiers',
    canonicalHref: '/admin/packages',
    searchTerms: ['tiers', 'legacy packages', 'bundles'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'rates',
    label: 'Category rate defaults',
    description: 'Manage labor, equipment, material, and target-margin defaults used by cost-driven price cards.',
    group: 'catalog-policy',
    icon: 'FileSpreadsheet',
    lifecycle: 'canonical',
    pathPattern: '/admin/rates',
    href: '/admin/rates',
    searchTerms: ['production rate', 'man hour', 'category rates', 'service pricing'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'rate-history',
    label: 'Rate history',
    description: 'Inspect prior rate-table versions and recorded changes.',
    group: 'advanced',
    icon: 'Clock',
    lifecycle: 'detail',
    pathPattern: '/admin/rates/versions',
    href: '/admin/rates/versions',
    searchTerms: ['versions', 'rate changes', 'pricing history'],
    visibility: SEARCH_ONLY,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'terms',
    label: 'Terms templates',
    description: 'Manage the terms and conditions attached to estimates.',
    group: 'catalog-policy',
    icon: 'FileText',
    lifecycle: 'canonical',
    pathPattern: '/admin/terms',
    href: '/admin/terms',
    searchTerms: ['conditions', 'contract language', 'proposal terms'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'seasons',
    label: 'Operational seasons',
    description: 'Define seasonal periods used by route templates.',
    group: 'catalog-policy',
    icon: 'CalendarCheck',
    lifecycle: 'canonical',
    pathPattern: '/admin/seasons',
    href: '/admin/seasons',
    searchTerms: ['green season', 'snow season', 'dates', 'route templates'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'serviceability',
    label: 'Serviceability',
    description: 'Define service zones and inspect quote-location decisions.',
    group: 'catalog-policy',
    icon: 'Target',
    lifecycle: 'canonical',
    pathPattern: '/admin/serviceability',
    href: '/admin/serviceability',
    searchTerms: ['map', 'polygon', 'zip code', 'service area', 'quote eligibility'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },

  // Field operations
  {
    id: 'labor-capacity',
    label: 'Labor & capacity',
    description: 'Compare contracted demand with available field labor.',
    group: 'field-operations',
    icon: 'Activity',
    lifecycle: 'canonical',
    pathPattern: '/admin/labor-capacity',
    href: '/admin/labor-capacity',
    searchTerms: ['hours', 'staffing', 'workload', 'forecast', 'demand'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'service-verification',
    label: 'Service verification',
    description: 'Review service evidence and verification outcomes.',
    group: 'field-operations',
    icon: 'ShieldCheck',
    lifecycle: 'canonical',
    pathPattern: '/admin/service-verify',
    href: '/admin/service-verify',
    searchTerms: ['service verify', 'evidence', 'photos', 'quality'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'measurements',
    label: 'Measurements',
    description: 'Inspect measurement-sidecar jobs and property measurement results.',
    group: 'field-operations',
    icon: 'LayoutGrid',
    lifecycle: 'canonical',
    pathPattern: '/admin/measurements',
    href: '/admin/measurements',
    searchTerms: ['property size', 'satellite', 'sidecar', 'square feet'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'compliance',
    label: 'Chemical compliance',
    description: 'Review Missouri chemical-application records and reporting.',
    group: 'field-operations',
    icon: 'ShieldCheck',
    lifecycle: 'canonical',
    pathPattern: '/admin/compliance',
    href: '/admin/compliance',
    searchTerms: ['mdoa', 'chemical application', '2 csr', 'pesticide records'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'shop-board',
    label: 'Shop board',
    description: 'Administer the operational board used for shop work.',
    group: 'field-operations',
    icon: 'KanbanSquare',
    lifecycle: 'canonical',
    pathPattern: '/admin/shop-board',
    href: '/admin/shop-board',
    searchTerms: ['kanban', 'shop work', 'tasks', 'board'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'subcontractors',
    label: 'Subcontractors',
    description: 'Manage subcontractor records, assignments, and status.',
    group: 'field-operations',
    icon: 'Building2',
    lifecycle: 'canonical',
    pathPattern: '/admin/subs',
    href: '/admin/subs',
    searchTerms: ['subs', 'contractors', 'vendors', 'assignments'],
    visibility: NAV_SEARCH,
    surface: 'route',
    audience: 'admin',
  },
  {
    id: 'subcontractor-payouts',
    label: 'Subcontractor payouts',
    description: 'Review payout details for a selected subcontractor.',
    group: 'field-operations',
    icon: 'Receipt',
    lifecycle: 'detail',
    pathPattern: '/admin/subs/[id]/payouts',
    searchTerms: ['sub payments', 'contractor payouts'],
    visibility: HIDDEN,
    surface: 'route',
    audience: 'admin',
  },

] as const satisfies readonly AdminRouteDefinition[];
